import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";
import { apiJson } from "../lib/queryClient";
import ExpoPreviewLink from "../components/ExpoPreviewLink";
import DeletionCountdownNotice from "../components/DeletionCountdownNotice";
import {
  euro,
  intervalSuffix,
  priceForInterval,
  useSubscription,
  type FeatureDef,
  type PackagesResponse,
} from "../lib/subscription";
import {
  handoffBannerHeading,
  isHandoffCountryChanged,
  isHandoffPackageMismatch,
  showHandoffBanner,
} from "../lib/checkoutHandoff";

type Interval = "month" | "year";

export default function Packages() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: sub } = useSubscription();
  const { data, isLoading, isError, error } = useQuery<PackagesResponse>({
    queryKey: ["/api/subscription/packages"],
  });

  const [interval, setInterval] = useState<Interval>("month");
  const [promo, setPromo] = useState("");
  // Basis-Land (Task #522): genau ein Land ist im Abo enthalten, Auswahl beim Kauf.
  const [baseCountry, setBaseCountry] = useState<string>("");
  const [busyPriceId, setBusyPriceId] = useState<string | null>(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Die § 356-Zustimmung ist an die Paketwahl gekoppelt: Beim Klick auf
  // „Auswählen" öffnet sich ein Bestätigungsdialog für genau dieses Paket; erst
  // nach ausdrücklicher Zustimmung wird der Stripe-Checkout gestartet.
  const [consentPriceId, setConsentPriceId] = useState<string | null>(null);

  const packages = data?.packages ?? [];
  const countryCatalog = data?.countryCatalog ?? [];
  // Mobile reicht die Auswahl per ?land=ISO an den Web-Checkout weiter.
  const landParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("land")
      : null;
  // Mobile reicht zusätzlich das gewählte Paket per ?paket=<id> weiter. Wegen
  // der gesetzlich nötigen § 356-Bestätigung kann der Checkout nicht automatisch
  // gestartet werden — stattdessen wird das passende Paket hervorgehoben und in
  // den Sichtbereich gescrollt.
  const paketParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("paket")
      : null;
  // Rückkehr aus dem Stripe-Checkout (abgebrochen) oder fehlgeschlagener
  // Direkt-Handoff: klare Meldung statt stiller Landung in den Einstellungen.
  const checkoutParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("checkout")
      : null;
  const checkoutNote =
    checkoutParam === "fehler"
      ? "Der Checkout konnte nicht gestartet werden. Bitte wähle dein Paket erneut."
      : checkoutParam === "abgebrochen"
        ? "Der Checkout wurde abgebrochen. Du kannst jederzeit erneut ein Paket wählen."
        : null;
  const [highlightPkgId, setHighlightPkgId] = useState<string | null>(null);
  // Wird gesetzt, sobald der Nutzer die App-Übernahme bewusst verwirft
  // ("Auswahl ändern"), damit das Bestätigungs-Banner verschwindet und die
  // freie Auswahl nicht mit einer veralteten Bestätigung überlagert wird.
  const [handoffDismissed, setHandoffDismissed] = useState(false);
  const pkgRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const effectiveBaseCountry =
    baseCountry ||
    (landParam ? landParam.toUpperCase() : "") ||
    data?.defaultBaseCountry ||
    "DE";
  const featureMap = useMemo(() => {
    const map = new Map<string, FeatureDef>();
    (data?.featureCatalog ?? []).forEach((f) => map.set(f.key, f));
    return map;
  }, [data?.featureCatalog]);

  // Aus der App übernommene Auswahl (?paket=/?land=) sichtbar bestätigen, damit
  // sich der Wechsel in den Browser nahtlos anfühlt. Wird nur angezeigt, wenn die
  // App tatsächlich eine Auswahl mitgegeben hat; sonst greift der normale Flow.
  const handoffPackage = paketParam
    ? packages.find((p) => p.id === paketParam) ?? null
    : null;
  const handoffPackagePrice = handoffPackage
    ? priceForInterval(handoffPackage, interval) ?? handoffPackage.prices[0] ?? null
    : null;
  const handoffCountryName =
    landParam && countryCatalog.length > 0
      ? countryCatalog.find((c) => c.iso === effectiveBaseCountry)?.name ?? null
      : null;
  // Ursprünglich aus der App übernommenes Land (zum Erkennen einer Änderung).
  const handoffLand = landParam ? landParam.toUpperCase() : null;
  // Hat der Nutzer das Land seit der Übernahme bewusst geändert? Dann ist die
  // Bezeichnung „aus der App" nicht mehr korrekt -> Banner wird umbenannt.
  const countryChanged = isHandoffCountryChanged(handoffLand, baseCountry);
  const showHandoffConfirm = showHandoffBanner({
    hasHandoffPackage: Boolean(handoffPackage),
    hasHandoffCountry: Boolean(handoffCountryName),
    dismissed: handoffDismissed,
  });

  const hasYearly = packages.some((p) =>
    p.prices.some((pr) => pr.interval === "year"),
  );
  const hasMonthly = packages.some((p) =>
    p.prices.some((pr) => pr.interval === "month"),
  );

  // Aus der App vorgewähltes Paket (?paket=) hervorheben + in den Sichtbereich
  // scrollen, sobald die Pakete geladen sind. Die Hervorhebung verblasst nach
  // kurzer Zeit wieder, damit die normale Auswahl nicht dauerhaft überlagert wird.
  useEffect(() => {
    if (!paketParam || packages.length === 0) return;
    const match = packages.find((p) => p.id === paketParam);
    if (!match) return;
    setHighlightPkgId(match.id);
    const el = pkgRefs.current[match.id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const timer = window.setTimeout(() => setHighlightPkgId(null), 4000);
    return () => window.clearTimeout(timer);
  }, [paketParam, packages]);

  // Bereits aktiv (z.B. Admin oder laufendes Abo) -> direkt in die App.
  if (sub?.entitlement?.active && sub.entitlement.source !== "none") {
    return <Navigate to="/" replace />;
  }

  // Schritt 1: Paket gewählt -> § 356-Bestätigung für genau dieses Paket öffnen.
  function handleSelect(priceId: string) {
    setActionError(null);
    // Weicht das gewählte Paket vom App-Handoff (?paket=) ab, ist das
    // Bestätigungs-Banner veraltet -> verwerfen, damit es nichts Falsches zeigt.
    if (paketParam) {
      const selectedPkg = packages.find((p) =>
        p.prices.some((pr) => pr.id === priceId),
      );
      if (isHandoffPackageMismatch(paketParam, selectedPkg?.id)) {
        setHandoffDismissed(true);
      }
    }
    setConsentPriceId(priceId);
  }

  // Schritt 2: Zustimmung erteilt -> Stripe-Checkout direkt starten. Die
  // Zustimmung wird serverseitig (withdrawalConsent) vorausgesetzt.
  async function confirmCheckout(priceId: string) {
    setActionError(null);
    setConsentPriceId(null);
    setBusyPriceId(priceId);
    try {
      const res = await apiJson<{ url: string }>(
        "POST",
        "/api/subscription/checkout",
        {
          priceId,
          promotionCode: promo.trim() || undefined,
          baseCountry: effectiveBaseCountry,
          withdrawalConsent: true,
        },
      );
      if (res.url) {
        window.location.href = res.url;
        return;
      }
      setActionError("Checkout konnte nicht gestartet werden.");
    } catch (err) {
      setActionError((err as Error).message || "Checkout fehlgeschlagen.");
    } finally {
      setBusyPriceId(null);
    }
  }

  async function handleTrial() {
    setActionError(null);
    setTrialBusy(true);
    try {
      await apiJson("POST", "/api/subscription/trial/start");
      await qc.invalidateQueries({ queryKey: ["/api/subscription/me"] });
      navigate("/", { replace: true });
    } catch (err) {
      setActionError((err as Error).message || "Test konnte nicht gestartet werden.");
    } finally {
      setTrialBusy(false);
    }
  }

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const trialAvailable = sub?.trial?.available;
  const trialUsed = sub?.trial?.used === true;
  // 72h -> 3 Tage; defensiv runden, falls der Server eine andere Dauer liefert.
  const trialDays = Math.max(1, Math.round((sub?.trialDurationHours ?? 72) / 24));

  return (
    <div className="auth-shell">
      <div className="auth-frame">
        <div className="px-6 pt-12 pb-2 text-center">
          <div className="brand-tile" aria-hidden="true" />
          <h1 className="text-[26px] font-bold tracking-tight mt-5 text-[var(--color-app-text)]">
            Wähle dein Paket
          </h1>
          <p className="text-[14px] text-[var(--color-app-text-secondary)] mt-1">
            Schalte alle Pre-Flight-Check-Funktionen frei. Jederzeit kündbar.
          </p>
        </div>

        <div className="px-5 pb-10 space-y-4">
          {/* Aus der App übernommene Auswahl sichtbar bestätigen (nahtloser
              Übergang vom mobilen Paywall-Flow in den Web-Checkout). */}
          {showHandoffConfirm ? (
            <div
              className="card p-4 ring-2 ring-[var(--color-brand-500)]"
              data-testid="banner-handoff-selection"
            >
              <div className="text-[12px] uppercase tracking-wider font-semibold text-[var(--color-brand-500)]">
                {handoffBannerHeading(countryChanged)}
              </div>
              <div className="mt-2 space-y-2">
                {handoffPackage ? (
                  <div
                    className="flex items-baseline justify-between gap-3"
                    data-testid="text-handoff-package"
                  >
                    <span className="text-[14px] text-[var(--color-app-text-secondary)]">
                      Paket
                    </span>
                    <span className="text-[14px] font-semibold text-[var(--color-app-text)] text-right">
                      {handoffPackage.name}
                      {handoffPackagePrice ? (
                        <span className="text-[var(--color-app-text-tertiary)] font-normal">
                          {" "}
                          · {euro(handoffPackagePrice.unitAmount, handoffPackagePrice.currency)}{" "}
                          {intervalSuffix(handoffPackagePrice.interval)}
                        </span>
                      ) : null}
                    </span>
                  </div>
                ) : null}
                {handoffCountryName ? (
                  <div
                    className="flex items-baseline justify-between gap-3"
                    data-testid="text-handoff-country"
                  >
                    <span className="text-[14px] text-[var(--color-app-text-secondary)]">
                      Enthaltenes Land
                    </span>
                    <span className="text-[14px] font-semibold text-[var(--color-app-text)] text-right">
                      {handoffCountryName}
                    </span>
                  </div>
                ) : null}
              </div>
              <p className="text-[12px] text-[var(--color-app-text-tertiary)] mt-3">
                {countryChanged
                  ? "Du hast deine Auswahl angepasst. Bitte bestätige sie und schließe die Buchung ab."
                  : "Bitte bestätige deine Auswahl und schließe die Buchung ab."}
              </p>
              <button
                type="button"
                onClick={() => setHandoffDismissed(true)}
                className="text-[12px] font-semibold text-[var(--color-brand-500)] hover:underline mt-2"
                data-testid="button-change-handoff-selection"
              >
                Auswahl ändern
              </button>
            </div>
          ) : null}

          {/* Intervall-Umschalter (nur wenn beide Intervalle existieren) */}
          {hasMonthly && hasYearly ? (
            <div
              className="seg-track flex items-center justify-center gap-1 p-1 rounded-2xl"
              data-testid="toggle-interval"
            >
              <button
                onClick={() => setInterval("month")}
                className={`seg-option flex-1 px-4 py-2 rounded-xl text-[14px] font-semibold transition ${
                  interval === "month" ? "seg-option-active" : ""
                }`}
                data-testid="button-interval-month"
              >
                Monatlich
              </button>
              <button
                onClick={() => setInterval("year")}
                className={`seg-option flex-1 px-4 py-2 rounded-xl text-[14px] font-semibold transition ${
                  interval === "year" ? "seg-option-active" : ""
                }`}
                data-testid="button-interval-year"
              >
                Jährlich
              </button>
            </div>
          ) : null}

          {/* Basis-Land (Task #522): genau ein Land ist im Abo enthalten. */}
          {countryCatalog.length > 0 ? (
            <div>
              <label className="label" htmlFor="base-country">
                Enthaltenes Land
              </label>
              <select
                id="base-country"
                className="input"
                value={effectiveBaseCountry}
                onChange={(e) => setBaseCountry(e.target.value)}
                data-testid="select-base-country"
              >
                {countryCatalog.map((c) => (
                  <option key={c.iso} value={c.iso}>
                    {c.name}
                  </option>
                ))}
              </select>
              <p className="text-[12px] text-[var(--color-app-text-tertiary)] mt-1">
                Jedes Abo enthält genau ein Land. Weitere Länder kannst du später
                einzeln dazubuchen.
              </p>
            </div>
          ) : null}

          {/* Rabattcode */}
          <div>
            <label className="label" htmlFor="promo">
              Rabattcode (optional)
            </label>
            <input
              id="promo"
              className="input"
              placeholder="z.B. WILLKOMMEN10"
              value={promo}
              onChange={(e) => setPromo(e.target.value.toUpperCase())}
              data-testid="input-promo"
            />
          </div>

          {actionError ? (
            <div
              className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] rounded-xl px-3 py-2"
              data-testid="text-error"
            >
              {actionError}
            </div>
          ) : null}

          {checkoutNote ? (
            <div
              className="text-sm text-[var(--color-app-text-secondary)] bg-[var(--color-app-surface-2,rgba(127,178,229,0.08))] rounded-xl px-3 py-2"
              data-testid="text-checkout-note"
            >
              {checkoutNote}
            </div>
          ) : null}

          <DeletionCountdownNotice className="mb-4" />

          {isLoading ? (
            <div className="py-10 text-center text-sm text-[var(--color-app-text-tertiary)]">
              Pakete werden geladen …
            </div>
          ) : isError ? (
            <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] rounded-xl px-3 py-2">
              {(error as Error)?.message || "Pakete konnten nicht geladen werden."}
            </div>
          ) : packages.length === 0 ? (
            <div className="py-10 text-center text-sm text-[var(--color-app-text-tertiary)]">
              Derzeit sind keine Pakete verfügbar.
            </div>
          ) : (
            <div className="space-y-4">
              {packages.map((pkg) => {
                const price =
                  priceForInterval(pkg, interval) ?? pkg.prices[0] ?? null;
                const busy = busyPriceId === price?.id;
                return (
                  <div
                    key={pkg.id}
                    ref={(el) => {
                      pkgRefs.current[pkg.id] = el;
                    }}
                    className={`card p-5 transition-shadow ${
                      highlightPkgId === pkg.id
                        ? "ring-2 ring-[var(--color-brand-500)]"
                        : ""
                    }`}
                    data-testid={`card-package-${pkg.id}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <h2 className="text-[18px] font-bold text-[var(--color-app-text)]">
                        {pkg.name}
                      </h2>
                      {price ? (
                        <div className="text-right">
                          <div className="text-[20px] font-bold text-[var(--color-app-text)]">
                            {euro(price.unitAmount, price.currency)}
                          </div>
                          <div className="text-[12px] text-[var(--color-app-text-tertiary)]">
                            {intervalSuffix(price.interval)}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {pkg.description ? (
                      <p className="text-[13.5px] text-[var(--color-app-text-secondary)] mt-1">
                        {pkg.description}
                      </p>
                    ) : null}

                    {pkg.features.length > 0 ? (
                      <ul className="mt-3 space-y-1.5">
                        {pkg.features.map((fk) => (
                          <li
                            key={fk}
                            className="flex items-start gap-2 text-[13.5px] text-[var(--color-app-text)]"
                          >
                            <Check
                              size={16}
                              className="text-[var(--color-go,#16a34a)] mt-0.5 shrink-0"
                            />
                            <span>{featureMap.get(fk)?.name ?? fk}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    <button
                      className="btn-primary w-full mt-4"
                      disabled={!price || busy || busyPriceId !== null}
                      onClick={() => price && handleSelect(price.id)}
                      data-testid={`button-select-${pkg.id}`}
                    >
                      {busy ? "Weiterleitung …" : "Auswählen"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Test-Paket: einmaliger Komplett-Test (voller Funktionsumfang). */}
          {trialUsed && !trialAvailable ? null : (
            <div
              className="card p-5 mt-4 ring-2 ring-[var(--color-brand-500)]"
              data-testid="card-package-trial"
            >
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-[18px] font-bold text-[var(--color-app-text)]">
                  Test-Paket
                </h2>
                <div className="text-right">
                  <div className="text-[20px] font-bold text-[var(--color-app-text)]">
                    Kostenlos
                  </div>
                  <div className="text-[12px] text-[var(--color-app-text-tertiary)]">
                    {trialDays} Tage
                  </div>
                </div>
              </div>
              <p className="text-[13.5px] text-[var(--color-app-text-secondary)] mt-1">
                Teste {trialDays} Tage lang den vollen Funktionsumfang aller Pakete
                (alle Funktionen, alle Länder). Einmalig pro Konto, danach
                kostenpflichtig.
              </p>
              <button
                className="btn-primary w-full mt-4"
                disabled={trialBusy || !trialAvailable}
                onClick={handleTrial}
                data-testid="button-trial"
              >
                {trialBusy ? "Test wird gestartet …" : "Test starten"}
              </button>
            </div>
          )}

          <ExpoPreviewLink className="mt-2" />

          {/* Konto-Fußzeile */}
          <div className="pt-6 mt-2 border-t border-[var(--color-app-border)] space-y-3">
            <div className="text-center">
              <div className="text-[12px] uppercase tracking-wider text-[var(--color-app-text-tertiary)]">
                Angemeldet als
              </div>
              <div className="text-[14px] font-semibold text-[var(--color-app-text)] truncate">
                {user?.name || user?.email || "—"}
              </div>
            </div>
            <div className="flex items-center justify-center gap-4 text-[13.5px]">
              <Link
                to="/abo"
                className="text-[var(--color-brand-500)] font-semibold hover:underline"
                data-testid="link-manage-subscription"
              >
                Abo verwalten
              </Link>
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 font-semibold text-[var(--color-nogo,#ef4444)] hover:underline"
                data-testid="button-logout"
              >
                <LogOut size={15} />
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </div>

      {consentPriceId ? (
        <div
          className="fixed inset-0 bg-black/50 grid place-items-center p-4 z-50"
          onClick={() => setConsentPriceId(null)}
        >
          <div
            className="card p-5 w-full max-w-md space-y-3"
            onClick={(e) => e.stopPropagation()}
            data-testid="modal-withdrawal-consent"
          >
            <h2 className="text-[17px] font-bold text-[var(--color-app-text)]">
              Vor der Buchung bestätigen
            </h2>
            <p className="text-[13.5px] text-[var(--color-app-text-secondary)] leading-relaxed">
              Ich verlange ausdrücklich, dass mit der Ausführung der digitalen
              Dienstleistung sofort begonnen wird, und bestätige meine Kenntnis,
              dass mein Widerrufsrecht mit Beginn der Ausführung erlischt (§ 356
              Abs. 5 BGB).
            </p>
            <div className="text-[12px] text-[var(--color-app-text-tertiary)]">
              Details:{" "}
              <a
                href="/widerruf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-brand-500)] hover:underline"
                data-testid="link-withdrawal-policy"
              >
                Widerrufsbelehrung
              </a>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button
                className="btn-secondary flex-1"
                onClick={() => setConsentPriceId(null)}
                data-testid="button-consent-cancel"
              >
                Abbrechen
              </button>
              <button
                className="btn-primary flex-1"
                onClick={() => confirmCheckout(consentPriceId)}
                data-testid="button-consent-confirm"
              >
                Bestätigen & zahlen
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
